import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  Skeleton,
  Paper,
  CircularProgress,
} from '@mui/material';
import { trpc } from '../../lib/trpc';
import { PlanSelector } from '../../components/billing/PlanSelector';
import { SubscriptionStatus } from '../../components/billing/SubscriptionStatus';
import type { CurrencyCode } from '../../lib/currency';

/**
 * Billing Page
 * Main page for managing subscription and viewing billing history
 */
export function BillingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSyncingCheckout, setIsSyncingCheckout] = useState(false);
  const syncAttemptedRef = useRef(false);

  // Fetch current subscription
  const {
    data: subscription,
    isLoading: isLoadingSubscription,
    refetch: refetchSubscription,
  } = trpc.billing.getSubscription.useQuery();

  // Create checkout session mutation
  const createCheckoutMutation = trpc.billing.createCheckoutSession.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      setErrorMessage(error.message);
    },
  });

  // Sync checkout session mutation (for webhook-less flow)
  const syncCheckoutMutation = trpc.billing.syncCheckoutSession.useMutation({
    onSuccess: (data) => {
      setIsSyncingCheckout(false);
      if (data.success) {
        setSuccessMessage('Your subscription has been activated successfully! Welcome to Pro.');
        refetchSubscription();
      } else {
        // Payment pending - this shouldn't normally happen but handle gracefully
        setErrorMessage('Payment is still being processed. Please refresh in a moment.');
      }
      // Clear the URL params
      setSearchParams({});
    },
    onError: (error) => {
      setIsSyncingCheckout(false);
      setErrorMessage(error.message || 'Failed to activate subscription. Please contact support.');
      // Clear the URL params
      setSearchParams({});
    },
  });

  // Handle URL params from Stripe redirect
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const sessionId = searchParams.get('session_id');

    // If we have a session_id from Stripe, sync the checkout session
    // This handles the webhook-less flow
    if (success === 'true' && sessionId && !syncAttemptedRef.current) {
      syncAttemptedRef.current = true;
      setIsSyncingCheckout(true);
      syncCheckoutMutation.mutate({ sessionId });
    } else if (success === 'true' && !sessionId) {
      // Legacy flow without session_id (webhook should have handled it)
      setSuccessMessage('Your subscription has been activated successfully! Welcome to Pro.');
      // Clear the URL params
      setSearchParams({});
      // Refetch subscription to get updated status
      refetchSubscription();
    } else if (canceled === 'true') {
      setErrorMessage('Checkout was canceled. No changes were made to your subscription.');
      // Clear the URL params
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, refetchSubscription, syncCheckoutMutation]);

  /**
   * Handle upgrade to Pro
   */
  const handleUpgrade = (interval: 'monthly' | 'yearly', currency: CurrencyCode) => {
    createCheckoutMutation.mutate({ interval, currency });
  };

  /**
   * Handle snackbar close
   */
  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  return (
    <Box>
      {/* Page Header */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Billing & Subscription
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your subscription plan and view billing history
      </Typography>

      {/* Success/Error Alerts */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Syncing Checkout State */}
      {isSyncingCheckout && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
          }}
        >
          <CircularProgress size={48} sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Activating your subscription...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we confirm your payment with Stripe.
          </Typography>
        </Box>
      )}

      {/* Loading State */}
      {isLoadingSubscription && !isSyncingCheckout && (
        <Box>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={100} />
          </Paper>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={200} />
          </Paper>
        </Box>
      )}

      {/* Subscription Content */}
      {!isLoadingSubscription && !isSyncingCheckout && subscription && (
        <>
          {/* Current Subscription Status */}
          <Box sx={{ mb: 4 }}>
            <SubscriptionStatus
              subscription={subscription}
              onRefresh={() => refetchSubscription()}
              onSubscribe={handleUpgrade}
              isSubscribeLoading={createCheckoutMutation.isLoading}
            />
          </Box>

          {/* Plan Selection */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Available Plans
            </Typography>
            <PlanSelector
              currentPlan={subscription.plan}
              onUpgrade={handleUpgrade}
              isLoading={createCheckoutMutation.isLoading}
            />
          </Paper>
        </>
      )}
    </Box>
  );
}

export default BillingPage;
